import { Check, ChevronLeft, ChevronRight, Sprout, Trash2 } from "lucide-react";
import { useMemo, useState } from "react";

import { formatTrayFraction, type CycleRow, type EventRow } from "../lib/api";
import { addDays, formatMonthDay, todayISO, type IsoDate } from "../lib/date";
import { useCompleteEvent, useCycles, useDeleteEvent, useEventsByDate } from "../lib/queries";
import { fonts, kindStyles, radius, shadow, tokens, trayColors } from "../styles/theme";
import { ConfirmButton } from "../components/ConfirmButton";
import { ErrorBanner } from "../components/ErrorBanner";
import { SectionLabel } from "../components/SectionLabel";
import { Skeleton } from "../components/Skeleton";
import { UndoToast } from "../components/UndoToast";

const ACTION_KINDS = ["seed", "soak", "uncover", "harvest", "reseed", "check"] as const;

export function TodayView() {
  const [date, setDate] = useState<IsoDate>(todayISO());
  const events = useEventsByDate(date);
  const cycles = useCycles({ active: true });
  const completeEvent = useCompleteEvent();
  const deleteEvent = useDeleteEvent();
  const [undoTarget, setUndoTarget] = useState<{ id: string; wasCompleted: boolean } | null>(null);
  const [mutationError, setMutationError] = useState<string | null>(null);

  const cycleById = useMemo(() => {
    const m = new Map<string, CycleRow>();
    for (const c of cycles.data ?? []) m.set(c.id, c);
    return m;
  }, [cycles.data]);

  const byTray = useMemo(() => {
    const m = new Map<string, CycleRow>();
    for (const c of cycles.data ?? []) {
      const prev = m.get(c.tray_id);
      if (!prev || new Date(c.started_at).getTime() > new Date(prev.started_at).getTime()) {
        m.set(c.tray_id, c);
      }
    }
    return m;
  }, [cycles.data]);

  const action = useMemo(() => {
    return (events.data ?? []).filter((e) => (ACTION_KINDS as readonly string[]).includes(e.kind));
  }, [events.data]);

  const notes = useMemo(() => {
    return (events.data ?? []).filter((e) => e.kind === "note");
  }, [events.data]);

  const doneCount = action.filter((e) => e.completed_at !== null).length;
  const pct = action.length ? Math.round((doneCount / action.length) * 100) : 0;
  const allDone = action.length > 0 && pct === 100;

  const shiftDay = (delta: number) => setDate((d) => addDays(d, delta));

  const handleToggle = (ev: EventRow) => {
    const wasCompleted = ev.completed_at !== null;
    completeEvent.mutate(
      { id: ev.id, completed: !wasCompleted },
      {
        onError: (err) => setMutationError(err.message),
      },
    );
    if (!wasCompleted) {
      setUndoTarget({ id: ev.id, wasCompleted });
    } else {
      setUndoTarget(null);
    }
  };

  const handleUndo = () => {
    if (!undoTarget) return;
    completeEvent.mutate(
      { id: undoTarget.id, completed: undoTarget.wasCompleted },
      { onError: (err) => setMutationError(err.message) },
    );
    setUndoTarget(null);
  };

  const weekday = new Date(`${date}T12:00:00Z`).toLocaleDateString("en-US", {
    weekday: "long",
    timeZone: "UTC",
  });

  return (
    <div>
      {mutationError && (
        <ErrorBanner message={mutationError} onDismiss={() => setMutationError(null)} />
      )}

      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: "20px",
          padding: "10px",
          background: tokens.card,
          border: `1px solid ${tokens.line}`,
          borderRadius: radius.md,
          boxShadow: shadow.xs,
        }}
      >
        <button
          type="button"
          aria-label="Previous day"
          onClick={() => shiftDay(-1)}
          style={iconBtn}
        >
          <ChevronLeft size={18} />
        </button>
        <div style={{ textAlign: "center", flex: 1 }}>
          <div
            style={{
              fontSize: "22px",
              color: tokens.ink,
              lineHeight: 1.1,
            }}
          >
            {weekday}, {formatMonthDay(date)}
          </div>
          <div
            style={{
              fontSize: "11px",
              color: tokens.muted,
              letterSpacing: "0.14em",
              textTransform: "uppercase",
              fontWeight: 600,
              marginTop: "4px",
            }}
          >
            {action.length === 0 ? "Rest day" : `${doneCount} of ${action.length} done · ${pct}%`}
          </div>
        </div>
        <button type="button" aria-label="Next day" onClick={() => shiftDay(1)} style={iconBtn}>
          <ChevronRight size={18} />
        </button>
      </div>

      {action.length > 0 && (
        <div
          style={{
            height: "8px",
            background: tokens.sunken,
            borderRadius: radius.pill,
            marginBottom: "20px",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              height: "100%",
              width: `${pct}%`,
              background: allDone ? tokens.sage : tokens.sageDeep,
              borderRadius: radius.pill,
              transition: "width 0.35s ease",
            }}
          />
        </div>
      )}

      {events.isLoading && <Skeleton count={4} height={76} />}
      {events.isError && (
        <ErrorBanner message={`Failed to load events: ${(events.error as Error).message}`} />
      )}

      {!events.isLoading && action.length === 0 && notes.length === 0 && <EmptyState />}

      {action.map((ev) => {
        const cycle = ev.cycle_id ? cycleById.get(ev.cycle_id) : null;
        const fraction = cycle?.tray_fraction ?? null;
        return (
          <TaskCard
            key={ev.id}
            event={ev}
            phase={cycle?.phase ?? trayPhase(ev, byTray)}
            fraction={fraction}
            onToggle={() => handleToggle(ev)}
            onDelete={() =>
              deleteEvent.mutate({ id: ev.id }, { onError: (err) => setMutationError(err.message) })
            }
          />
        );
      })}

      {notes.length > 0 && (
        <div style={{ marginTop: "28px" }}>
          <SectionLabel>Notes</SectionLabel>
          {notes.map((ev) => (
            <div
              key={ev.id}
              style={{
                padding: "14px 16px 14px 18px",
                background: tokens.card,
                border: `1px solid ${tokens.line}`,
                borderLeft: `3px solid ${tokens.amber}`,
                borderRadius: radius.md,
                color: tokens.inkSoft,
                fontSize: "14px",
                marginBottom: "10px",
                lineHeight: 1.5,
                boxShadow: shadow.xs,
              }}
            >
              {ev.text}
            </div>
          ))}
        </div>
      )}

      {undoTarget && (
        <UndoToast
          label="Marked complete."
          onUndo={handleUndo}
          onDismiss={() => setUndoTarget(null)}
        />
      )}
    </div>
  );
}

function trayPhase(ev: EventRow, byTray: Map<string, CycleRow>) {
  if (!ev.tray_id) return null;
  return byTray.get(ev.tray_id)?.phase ?? null;
}

function TaskCard({
  event,
  phase,
  fraction,
  onToggle,
  onDelete,
}: {
  event: EventRow;
  phase: string | null;
  fraction: number | null;
  onToggle: () => void;
  onDelete: () => void;
}) {
  const done = event.completed_at !== null;
  const style = kindStyles[event.kind];
  const trayColor = event.tray_id ? (trayColors[event.tray_id] ?? tokens.faint) : tokens.faint;
  const isPartial = fraction !== null && fraction < 0.999;

  return (
    <div
      style={{
        display: "flex",
        gap: "12px",
        padding: "14px 14px 14px 20px",
        background: done ? tokens.sunken : tokens.card,
        border: `1px solid ${tokens.line}`,
        borderRadius: radius.md,
        marginBottom: "10px",
        opacity: done ? 0.7 : 1,
        position: "relative",
        alignItems: "flex-start",
        boxShadow: done ? "none" : shadow.xs,
        transition: "opacity 0.15s ease, background 0.15s ease",
      }}
    >
      {event.tray_id && (
        <div
          aria-hidden
          style={{
            position: "absolute",
            left: 0,
            top: "14px",
            bottom: "14px",
            width: "4px",
            background: trayColor,
            borderRadius: radius.pill,
          }}
        />
      )}
      <button
        type="button"
        onClick={onToggle}
        aria-label={done ? "Mark incomplete" : "Mark complete"}
        style={{
          background: "transparent",
          border: "none",
          minWidth: "44px",
          minHeight: "44px",
          padding: 0,
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <span
          aria-hidden
          style={{
            width: "26px",
            height: "26px",
            borderRadius: radius.pill,
            border: `2px solid ${done ? tokens.sage : tokens.lineStrong}`,
            background: done ? tokens.sage : tokens.card,
            color: "#fff",
            display: "grid",
            placeItems: "center",
            transition: "background 0.15s ease, border-color 0.15s ease, color 0.15s ease",
          }}
        >
          {done && <Check size={16} strokeWidth={3} />}
        </span>
      </button>
      <div style={{ flex: 1, minWidth: 0, paddingTop: "2px" }}>
        <div
          style={{
            display: "flex",
            gap: "8px",
            alignItems: "center",
            marginBottom: "6px",
            flexWrap: "wrap",
          }}
        >
          <span
            style={{
              fontSize: "11px",
              fontWeight: 600,
              letterSpacing: "0.03em",
              padding: "3px 9px",
              background: style.bg,
              color: style.fg,
              borderRadius: radius.pill,
            }}
          >
            {style.label.charAt(0) + style.label.slice(1).toLowerCase()}
          </span>
          {event.tray_id && (
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "6px",
                fontSize: "11px",
                color: tokens.muted,
                letterSpacing: "0.06em",
                fontWeight: 600,
                textTransform: "uppercase",
              }}
            >
              <span
                aria-hidden
                style={{
                  width: "8px",
                  height: "8px",
                  borderRadius: radius.pill,
                  background: trayColor,
                }}
              />
              Tray {event.tray_id}
            </span>
          )}
          {phase && (
            <span
              style={{
                fontSize: "11px",
                color: tokens.muted,
                letterSpacing: "0.06em",
                textTransform: "uppercase",
                fontWeight: 600,
              }}
            >
              {phase}
            </span>
          )}
          {event.crop && (
            <span style={{ fontSize: "12px", color: tokens.inkSoft }}>
              {event.crop}
              {isPartial && (
                <span style={{ color: tokens.muted }}> — {formatTrayFraction(fraction!)} tray</span>
              )}
            </span>
          )}
          {event.delivery === 1 && (
            <span
              style={{
                fontSize: "11px",
                fontWeight: 600,
                letterSpacing: "0.02em",
                padding: "3px 9px",
                background: tokens.terracotta,
                color: "#fff",
                borderRadius: radius.pill,
                marginLeft: "auto",
              }}
            >
              Delivery
            </span>
          )}
        </div>
        <div
          style={{
            fontSize: "15px",
            color: done ? tokens.muted : tokens.ink,
            textDecoration: done ? "line-through" : "none",
            lineHeight: 1.4,
          }}
        >
          {event.text}
        </div>
      </div>
      <ConfirmButton
        ariaLabel="Delete event"
        onConfirm={onDelete}
        confirmLabel={<span style={{ color: tokens.coral, fontWeight: 600 }}>Delete?</span>}
        style={{
          background: "transparent",
          border: "none",
          color: tokens.faint,
          padding: "0 8px",
          fontSize: "12px",
          fontFamily: fonts.body,
          display: "flex",
          alignItems: "center",
          borderRadius: radius.sm,
        }}
        confirmStyle={{ color: tokens.coral }}
      >
        <Trash2 size={16} />
      </ConfirmButton>
    </div>
  );
}

function EmptyState() {
  return (
    <div
      style={{
        textAlign: "center",
        padding: "56px 20px",
        background: tokens.card,
        border: `1px solid ${tokens.line}`,
        borderRadius: radius.lg,
        boxShadow: shadow.xs,
      }}
    >
      <div
        style={{
          width: "56px",
          height: "56px",
          margin: "0 auto 16px",
          borderRadius: radius.pill,
          background: tokens.sageSoft,
          color: tokens.sageDeep,
          display: "grid",
          placeItems: "center",
        }}
      >
        <Sprout size={28} strokeWidth={1.5} />
      </div>
      <div
        style={{
          fontSize: "22px",
          color: tokens.ink,
          lineHeight: 1.2,
        }}
      >
        Nothing scheduled.
      </div>
      <div
        style={{
          fontSize: "14px",
          color: tokens.muted,
          marginTop: "6px",
        }}
      >
        A quiet day on the rack.
      </div>
    </div>
  );
}

const iconBtn = {
  background: "transparent",
  border: `1px solid ${tokens.line}`,
  color: tokens.muted,
  width: "44px",
  height: "44px",
  borderRadius: radius.sm,
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontFamily: "inherit",
  transition: "background 0.15s ease, color 0.15s ease",
} as const;
